import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Lock, Briefcase, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

interface CuratedJob {
  id: string;
  company_name: string;
  title: string;
  work_model: string | null;
  description: string;
  external_link: string;
}

interface JobsPreviewTeaserProps {
  onRegisterClick: () => void;
}

export function JobsPreviewTeaser({ onRegisterClick }: JobsPreviewTeaserProps) {
  const { data: jobs, isLoading } = useQuery({
    queryKey: ['preview-curated-jobs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('curated_jobs')
        .select('*')
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data as CuratedJob[];
    },
  });

  const handleViewJob = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      onRegisterClick();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
    );
  }

  if (!jobs || jobs.length === 0) {
    return null;
  }

  const [firstJob, ...lockedJobs] = jobs;

  return (
      <div className="space-y-3 max-w-full overflow-hidden">
      {/* Container com position relative para overlay */}
      <div className="relative">
        {/* VAGA 1 - Visível */}
        <div className="w-full mb-3">
          <div className="flex items-center gap-3 sm:gap-4 bg-white rounded-lg shadow-md p-3 sm:p-4 border border-border hover:shadow-lg transition-shadow max-w-full">
            {/* Logo/Ícone */}
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
            </div>
            
            {/* Conteúdo da vaga */}
            <div className="flex-1 min-w-0">
              <Badge className="mb-1 bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200 text-[10px] sm:text-xs px-2 py-0.5">
                ✨ Vaga Curada
              </Badge>
              <h3 className="font-bold text-sm sm:text-base mb-1 truncate text-foreground">
                {firstJob.title}
              </h3>
              <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
                <span className="font-medium">{firstJob.company_name}</span>
                {firstJob.work_model && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {firstJob.work_model}
                    </span>
                  </>
                )}
              </div>
            </div>
            
            {/* Botão Ver Vaga */}
            <Button 
              size="sm" 
              onClick={handleViewJob}
              className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4"
            >
              Ver Vaga
            </Button>
          </div>
        </div>

        {/* VAGA 2 - Com Blur (dados reais) */}
        {lockedJobs[0] && (
          <div className="w-full mb-3" style={{ filter: 'blur(4px)', opacity: 0.8 }}>
            <div className="flex items-center gap-3 sm:gap-4 bg-white rounded-lg shadow-md p-3 sm:p-4 border border-border max-w-full">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <Badge className="mb-1 bg-purple-100 text-purple-700 border-purple-200 text-[10px] sm:text-xs px-2 py-0.5">
                  ✨ Vaga Curada
                </Badge>
                <h3 className="font-bold text-sm sm:text-base mb-1 truncate text-foreground">
                  {lockedJobs[0].title}
                </h3>
                <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
                  <span className="font-medium">{lockedJobs[0].company_name}</span>
                  {lockedJobs[0].work_model && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {lockedJobs[0].work_model}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Button size="sm" className="bg-purple-600 text-white flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                Ver Vaga
              </Button>
            </div>
          </div>
        )}

        {/* VAGA 3 - Com Mais Blur (dados reais) */}
        {lockedJobs[1] && (
          <div className="w-full" style={{ filter: 'blur(6px)', opacity: 0.6 }}>
            <div className="flex items-center gap-3 sm:gap-4 bg-white rounded-lg shadow-md p-3 sm:p-4 border border-border max-w-full">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <Badge className="mb-1 bg-purple-100 text-purple-700 border-purple-200 text-[10px] sm:text-xs px-2 py-0.5">
                  ✨ Vaga Curada
                </Badge>
                <h3 className="font-bold text-sm sm:text-base mb-1 truncate text-foreground">
                  {lockedJobs[1].title}
                </h3>
                <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
                  <span className="font-medium">{lockedJobs[1].company_name}</span>
                  {lockedJobs[1].work_model && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {lockedJobs[1].work_model}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Button size="sm" className="bg-purple-600 text-white flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                Ver Vaga
              </Button>
            </div>
          </div>
        )}

        {/* Overlay de bloqueio sobre vagas 2 e 3 */}
        {lockedJobs.length > 0 && (
          <div 
            className="absolute top-[calc(50%+20px)] left-0 right-0 flex items-center justify-center z-10 cursor-pointer"
            onClick={onRegisterClick}
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-6 py-3 shadow-lg border border-purple-200 hover:bg-white transition-colors">
              <Lock className="h-6 w-6 text-purple-600 mx-auto mb-1" />
              <p className="text-sm font-semibold text-gray-800">
                🔒 Cadastre-se Grátis
              </p>
            </div>
          </div>
        )}
      </div>

      {/* CTA Button abaixo das vagas */}
      <Button 
        size="sm" 
        className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-2 text-sm sm:text-base py-2 sm:py-2.5"
        onClick={onRegisterClick}
      >
        Ver Todas as Vagas →
      </Button>
    </div>
  );
}
